import axios from "axios";
import toast from "react-hot-toast";
import useAuthStore from "./Zustand/Store/useAuthStore";

function isInactiveOrgAccessMessage(msg) {
  const msgLower = typeof msg === "string" ? msg.toLowerCase() : "";
  return (
    msgLower.includes("inactive for this organization") ||
    msgLower.includes("disabled for your organization") ||
    msgLower.includes("not enabled for") ||
    msgLower.includes("not linked to this organization") ||
    msgLower.includes("not linked to any organization") ||
    msgLower.includes("not active for your account") ||
    msgLower.includes("organization is not available for this application") ||
    msgLower.includes("subscription is inactive") ||
    msgLower.includes("subscription has expired")
  );
}

function shouldForceLogoutFromAccessError(error) {
  const status = error.response?.status;
  if (status !== 403) return false;
  const data = error.response?.data || {};
  if (data.force_logout === true || data.access_revoked === true) {
    // Billing/upgrade flows should not hard-logout to /login.
    if (data.upgrade_required === true || data.billing_only === true) {
      return false;
    }
    return true;
  }
  return isInactiveOrgAccessMessage(data.message);
}

function forceLogoutOnInactiveAccess(msg) {
  if (msg) {
    toast.error(msg);
  }
  useAuthStore.getState().logout();
  if (
    typeof window !== "undefined" &&
    !String(window.location.pathname).includes("/login")
  ) {
    window.location.href = "/login";
  }
}

/**
 * When backend blocks requests (inactive org user), surface the API message,
 * clear session, and send the user to login. Skips `/api/auth/login` so Login.js
 * can show the same message without double toasts / forced redirect loops.
 */
export function setupAxiosAuthInterceptor() {
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error.response?.status;
      const url = error.config?.url ?? "";
      const msg = error.response?.data?.message;

      const isLoginUrl =
        url.includes("/api/auth/login") || url.includes("/auth/login");

      // Inactive org / revoked application access (403)
      const isAccessDenied =
        status === 403 && shouldForceLogoutFromAccessError(error);
      if (isAccessDenied && !isLoginUrl) {
        forceLogoutOnInactiveAccess(msg);
        return Promise.reject(error);
      }

      // Expired / invalid token (401) — only force logout for authenticated routes,
      // NOT for login/token-login itself to avoid redirect loops.
      if (
        status === 401 &&
        !isLoginUrl &&
        !url.includes("/api/auth/token-login")
      ) {
        const token = localStorage.getItem("token");
        // Only logout if we actually had a token (not just an unauthenticated request)
        if (token) {
          toast.error("Session expired. Please log in again.");
          useAuthStore.getState().logout();
          if (
            typeof window !== "undefined" &&
            !String(window.location.pathname).includes("/login")
          ) {
            window.location.href = "/login";
          }
        }
      }

      return Promise.reject(error);
    }
  );
}

export { isInactiveOrgAccessMessage, forceLogoutOnInactiveAccess, shouldForceLogoutFromAccessError };
